import {
  Component, OnInit, inject, signal, CUSTOM_ELEMENTS_SCHEMA, DestroyRef
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  FormBuilder, Validators, ReactiveFormsModule, FormArray, FormControl
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { pairwise, startWith } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { ModuleService } from '../../../shared/services/module.service';
import { AppButton } from '../../../shared/components/app-button/app-button';
import { AppInput } from '../../../shared/components/app-input/app-input';
import { AppSelect, SelectOption } from '../../../shared/components/app-select/app-select';
import { LoadingService } from '../../../core/services/loading.service';
import { AppSectionHeader } from "../../../shared/components/app-section-header/app-section-header";



@Component({
  selector: 'app-system-module-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppButton, AppInput, AppSelect, AppSectionHeader],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './system-module-form.html',
  styleUrl: './system-module-form.css'
})
export class SystemModuleForm implements OnInit {
  private moduleService = inject(ModuleService);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private loadingService = inject(LoadingService);

  paramsSignal = toSignal(this.route.params);
  querySignal = toSignal(this.route.queryParams);

  get moduleIdParam(): string | null {
    return this.paramsSignal()?.['id'] || null;
  }

  form = this.fb.group({
    key: ['', [Validators.required, Validators.pattern(/^[a-z][a-z0-9-]*$/)]],
    label: ['', Validators.required],
    icon: ['', Validators.required],
    route: ['', Validators.required],
    group: ['admin', Validators.required],
    order: [0],
    permissions: this.fb.array([]),
    isActive: [true],
  });

  isEdit = signal(false);
  saving = signal(false);
  error = signal('');

  groupOptions = signal<SelectOption[]>([]);
  commonActionsMetadata = signal<any[]>([]);

  get permissionsArray(): FormArray {
    return this.form.get('permissions') as FormArray;
  }

  /** Error visible para el campo key */
  get keyError(): string {
    const ctrl = this.form.get('key');
    if (!ctrl?.touched && !ctrl?.dirty) return '';
    if (ctrl.hasError('required')) return 'Requerido';
    if (ctrl.hasError('pattern')) return 'Solo minúsculas, números y _ (ej: patients)';
    return '';
  }

  /** Error genérico para campos required */
  fieldError(name: string): string {
    const ctrl = this.form.get(name);
    if (!ctrl?.touched && !ctrl?.dirty) return '';
    return ctrl?.hasError('required') ? 'Este campo es requerido' : '';
  }

  ngOnInit() {
    const id = this.moduleIdParam;
    if (id) {
      this.isEdit.set(true);
      this.loadingService.showLoading('Cargando Módulo', 'Recuperando configuración técnica...');
      this.moduleService.getById(id).subscribe({
        next: (mod: any) => {
          this.form.patchValue({
            key: mod.key, label: mod.label, icon: mod.icon,
            route: mod.route, group: mod.group, order: mod.order,
            isActive: mod.isActive
          });
          this.permissionsArray.clear();
          (mod.permissions ?? []).forEach((p: string) =>
            this.permissionsArray.push(new FormControl(p))
          );
          this.loadingService.hideLoading();
        },
        error: () => this.loadingService.hideLoading()
      });
    }

    // ── Cargar Metadata Dinámica ───────────────────────────────────────────
    this.moduleService.getMetadata().subscribe({
      next: (meta) => {
        this.groupOptions.set(meta.groups.map(g => ({ label: g.label, value: g.key })));
        this.commonActionsMetadata.set(meta.actions);
      }
    });

    // ── Sincronizar permisos cuando cambia el key ──────────────────────────
    // oldKey → newKey: renombra permisos que coincidan, descarta los que no
    this.form.get('key')!.valueChanges.pipe(
      startWith(this.form.get('key')!.value as string),
      pairwise(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([oldKey, newKey]) => {
      // Si el key se borró → limpiar todos los permisos
      if (!newKey) {
        this.permissionsArray.clear();
        return;
      }
      if (!oldKey || oldKey === newKey) return;
      const updated: string[] = [];
      for (const ctrl of this.permissionsArray.controls) {
        const val: string = ctrl.value;
        if (val.startsWith(`${oldKey}:`)) {
          // patients:list → appointments:list
          updated.push(`${newKey}:${val.slice(oldKey.length + 1)}`);
        }
        // permisos de otro prefijo (custom) → se descartan automáticamente
      }
      this.permissionsArray.clear();
      updated.forEach(p => this.permissionsArray.push(new FormControl(p)));
    });
  }

  goBack() {
    this.router.navigate(['/app/system-modules']);
  }

  // ── Permisos ──────────────────────────────────────────────────────────────

  isPermSelected(actionKey: string): boolean {
    const key = this.form.get('key')?.value;
    return key ? this.permissionsArray.value.includes(`${key}:${actionKey}`) : false;
  }

  toggleAction(actionKey: string) {
    const key = this.form.get('key')?.value;
    if (!key) return;
    const perm = `${key}:${actionKey}`;
    const idx = this.permissionsArray.controls.findIndex(c => c.value === perm);
    if (idx > -1) {
      this.permissionsArray.removeAt(idx);
    } else {
      this.permissionsArray.push(new FormControl(perm));
    }
  }

  addCommonActions() {
    const key = this.form.get('key')?.value;
    if (!key) return;

    // Tomar las primeras 4 acciones (list, create, edit, delete) de la metadata dinámica
    const essentials = this.commonActionsMetadata().slice(0, 4);
    essentials.forEach(action => {
      const perm = `${key}:${action.key}`;
      if (!this.permissionsArray.value.includes(perm))
        this.permissionsArray.push(new FormControl(perm));
    });
  }

  addCustom(value: string) {
    const perm = value?.trim();
    if (!perm || this.permissionsArray.value.includes(perm)) return;
    this.permissionsArray.push(new FormControl(perm));
  }

  removePermission(index: number) {
    this.permissionsArray.removeAt(index);
  }

  // ── Guardar ───────────────────────────────────────────────────────────────

  save() {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    this.saving.set(true);
    this.error.set('');
    const id = this.moduleIdParam;
    const v = this.form.getRawValue();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      key: v.key || '', label: v.label || '', icon: v.icon || '',
      route: v.route || '', group: v.group || 'admin',
      order: v.order ?? 0, isActive: v.isActive ?? true,
      permissions: this.permissionsArray.value as string[],
    };
    const req = id ? this.moduleService.update(id, payload) : this.moduleService.create(payload);
    req.subscribe({
      next: () => { this.saving.set(false); this.router.navigate(['/app/system-modules']); },
      error: (err: any) => {
        this.saving.set(false);
        this.error.set(err?.error?.message ?? 'Error al guardar el módulo');
      },
    });
  }
}
